import { ReactNode, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Text, Button } from '@belong/ui';
import Field from 'components/Field/Field';
import GreenInfoBox from 'components/GreenInfoBox/GreenInfoBox';
import { MONEY_FORMAT, formatMoney } from 'components/Money/Money';
import {
  SELECTOR_TYPES,
  SelectorFinalFormAdapter,
} from 'components/Selector/Selector';
import { useModal } from 'hooks/useModal';
import { DepositFAQs } from 'lease-signing-flow-v2/components/deposit-faqs/deposit-faqs';
import { StepFormLayout } from 'lease-signing-flow-v2/components/step-form-layout/step-form-layout';
import { DEPOSIT_TYPES } from 'lease-signing-flow-v2/constants/deposit';
import { LEASE_SIGNING_FLOW_STRINGS } from 'lease-signing-flow-v2/constants/strings';
import { selectSelectedLeaseDepositType } from 'lease-signing-flow-v2/store/deposit';
import { WearAndTearModal } from 'pages/PostInspectionFlow/steps/Improvements/Responsiblity/WearAndTearModal/WearAndTearModal';
import { formatString } from 'strings';
import { required } from 'utils/validation';

type DepositTypeOptionProps = {
  title: ReactNode;
  children: ReactNode;
};

function DepositTypeOption({ title, children }: DepositTypeOptionProps) {
  return (
    <div className="flex flex-col items-center justify-center">
      <div className="flex items-baseline">
        <Text variant="h3" fontWeight="semibold">
          {title}
        </Text>
      </div>
      {children}
    </div>
  );
}

type PetDepositProps = {
  amount: number;
  type: (typeof DEPOSIT_TYPES)[keyof typeof DEPOSIT_TYPES];
};

function PetDeposit({ amount, type }: PetDepositProps) {
  const petDepositText = `(Including ${formatMoney(amount, MONEY_FORMAT.DOLLARS_ONLYIF_CENTS)} Pet Deposit)`;
  const petFeeText = `(Non refundable + ${formatMoney(amount, MONEY_FORMAT.DOLLARS_ONLYIF_CENTS)}/mo Pet Fee)`;

  return (
    <Text variant="p1" className="mt-0 md:mt-2xs">
      {type === DEPOSIT_TYPES.REFUNDABLE && petDepositText}
      {type === DEPOSIT_TYPES.FEE && petFeeText}
    </Text>
  );
}

type Props = {
  currentStep: any;
  lease?: any;
  onSubmit: (values?: any) => void;
  setLoading: (isLoading: boolean) => void;
  steps: any;
};

export const DepositStep = ({
  currentStep,
  lease,
  onSubmit,
  setLoading: _setLoading,
  steps,
}: Props) => {
  // `useModal` now declares its own tuple, so this no longer needs a cast to
  // recover the element types.
  const [isModalOpen, openModal, closeModal] = useModal();
  const _dispatch = useDispatch();
  const selectedDepositType = useSelector(selectSelectedLeaseDepositType);
  const isRenewal = lease?.leaseInfo?.basicInfo?.renewalNumber > 0;

  // Map deposit options from currentStep response
  const depositTypeOptions = useMemo(() => {
    if (!currentStep?.depositOptions?.options) {
      return [];
    }

    return currentStep.depositOptions.options.map((option: any) => ({
      depositType: option.depositType,
      frequency: option.frequency,
      amount: option.amount,
      petAmount: option.petAmount,
    }));
  }, [currentStep?.depositOptions]);

  const getInitialValues = () => {
    return {
      depositType:
        currentStep?.depositOptions?.selectedDepositType || selectedDepositType,
    };
  };

  const getDepositTypeOptionButtons = () => {
    const depositTypeOptionButtons: { label: ReactNode; key: string }[] = [];
    if (!depositTypeOptions || depositTypeOptions.length === 0) {
      return depositTypeOptionButtons;
    }

    const refundableOption = depositTypeOptions.find(
      (option: any) => option.depositType === DEPOSIT_TYPES.REFUNDABLE,
    );
    const feeOption = depositTypeOptions.find(
      (option: any) => option.depositType === DEPOSIT_TYPES.FEE,
    );

    if (refundableOption) {
      depositTypeOptionButtons.push({
        label: (
          <DepositTypeOption
            title={formatMoney(
              refundableOption.amount,
              MONEY_FORMAT.DOLLARS_ONLYIF_CENTS,
            )}
          >
            <Text className="mt-0 md:mt-2xs">
              {LEASE_SIGNING_FLOW_STRINGS['deposit.refundable']}
            </Text>
            {refundableOption.petAmount ? (
              <PetDeposit
                amount={refundableOption.petAmount}
                type={DEPOSIT_TYPES.REFUNDABLE}
              />
            ) : null}
          </DepositTypeOption>
        ),
        key: refundableOption.depositType,
      });
    }
    if (feeOption) {
      depositTypeOptionButtons.push({
        label: (
          <DepositTypeOption
            title={`${formatMoney(feeOption.amount, MONEY_FORMAT.DOLLARS_ONLYIF_CENTS)}/Mo.`}
          >
            <Text className="mt-0 md:mt-2xs">
              {LEASE_SIGNING_FLOW_STRINGS['deposit.fee']}
            </Text>
            {feeOption.petAmount ? (
              <PetDeposit
                amount={feeOption.petAmount}
                type={DEPOSIT_TYPES.FEE}
              />
            ) : (
              <Text variant="p1" className="mt-0 md:mt-2xs">
                {LEASE_SIGNING_FLOW_STRINGS['deposit.fee.disclaimer']}
              </Text>
            )}
          </DepositTypeOption>
        ),
        key: feeOption.depositType,
      });
    }

    return depositTypeOptionButtons;
  };

  const handleSubmit = async (values: any) => {
    onSubmit(values);
  };

  const depositFeeDescription = isRenewal
    ? LEASE_SIGNING_FLOW_STRINGS['deposit.renewal.fee.description']
    : LEASE_SIGNING_FLOW_STRINGS['deposit.fee.description'];
  const refundableDepositDescription = isRenewal
    ? LEASE_SIGNING_FLOW_STRINGS['deposit.renewal.refundable.description']
    : LEASE_SIGNING_FLOW_STRINGS['deposit.refundable.description'];

  return (
    <StepFormLayout
      currentStep={currentStep}
      initialValues={getInitialValues()}
      onSubmit={handleSubmit}
      previousContent={null}
      steps={steps}
      title={
        isRenewal
          ? LEASE_SIGNING_FLOW_STRINGS['deposit.renewal.title']
          : LEASE_SIGNING_FLOW_STRINGS['deposit.title']
      }
      subTitle={
        isRenewal
          ? LEASE_SIGNING_FLOW_STRINGS['deposit.renewal.subtitle']
          : LEASE_SIGNING_FLOW_STRINGS['deposit.subtitle']
      }
      getForm={({ values }: { values: any }) => {
        const { depositType } = values;

        const isFeeDeposit = depositType === DEPOSIT_TYPES.FEE;

        return (
          <>
            <div className="my-lg">
              <DepositFAQs isRenewal={isRenewal} />
            </div>
            <Field
              buttons={getDepositTypeOptionButtons()}
              component={SelectorFinalFormAdapter}
              fluid
              name="depositType"
              type={SELECTOR_TYPES.SELECTOR}
              validate={required}
              verticalSpaceBetween={30}
            />
            {depositType && (
              <GreenInfoBox cssClassNames={['-mt-xs']}>
                <Text>
                  {formatString(
                    isFeeDeposit
                      ? depositFeeDescription
                      : refundableDepositDescription,
                    {
                      wearAndTear: (
                        <Button variant="text" onClick={() => openModal()}>
                          wear and tear
                        </Button>
                      ),
                    },
                  )}
                </Text>
              </GreenInfoBox>
            )}
            <WearAndTearModal
              initialValueIndex={0}
              onHide={closeModal}
              show={isModalOpen}
            />
          </>
        );
      }}
    />
  );
};
